import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, Phone, Mail, UserCheck, UserX, Edit, Trash2, MoreVertical, Key, UserCog, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeesPage = () => {
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    phone: '',
    email: '',
    salary: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      toast.error('Error fetching employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingEmployee) {
        const updateData = { ...formData };
        delete updateData.password;
        delete updateData.username;
        updateData.salary = parseFloat(updateData.salary);
        await axios.put(`${API}/employees/${editingEmployee.id}`, updateData);
        toast.success('Employee updated successfully');
      } else {
        await axios.post(`${API}/auth/register`, {
          ...formData,
          role: 'employee',
          salary: parseFloat(formData.salary)
        });
        toast.success('Employee created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving employee');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      username: employee.username,
      password: '',
      name: employee.name,
      phone: employee.phone || '',
      email: employee.email || '',
      salary: employee.salary?.toString() || '0'
    });
    setIsDialogOpen(true);
  };

  const handleDeactivate = async (employeeId) => {
    if (!window.confirm('Are you sure you want to deactivate this employee?')) return;
    try {
      await axios.delete(`${API}/employees/${employeeId}`);
      toast.success('Employee deactivated');
      fetchEmployees();
    } catch (error) {
      toast.error('Error deactivating employee');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/change-employee-password`, {
        employee_id: selectedEmployee.id,
        new_password: newPassword
      });
      toast.success(`Password changed for ${selectedEmployee.name}`);
      setIsPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedEmployee(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error changing password');
    }
  };

  const handleDeleteEmployee = async () => {
    try {
      await axios.delete(`${API}/admin/delete-employee/${selectedEmployee.id}`);
      toast.success(`${selectedEmployee.name} deleted permanently`);
      setIsDeleteDialogOpen(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting employee');
    }
  };

  const handleReactivate = async (employee) => {
    try {
      await axios.put(`${API}/employees/${employee.id}`, { is_active: true });
      toast.success(`${employee.name} reactivated`);
      fetchEmployees();
    } catch (error) {
      toast.error('Error reactivating employee');
    }
  };

  const handleToggleAdmin = async (employee) => {
    const newRole = employee.role === 'admin' ? 'employee' : 'admin';
    const action = newRole === 'admin' ? 'Grant Admin Access' : 'Remove Admin Access';
    
    if (!window.confirm(`${action} for ${employee.name}?`)) return;
    
    try {
      await axios.put(`${API}/employees/${employee.id}`, { role: newRole });
      toast.success(`${employee.name} is now ${newRole === 'admin' ? 'an Admin' : 'an Employee'}`);
      fetchEmployees();
    } catch (error) {
      toast.error('Error updating role');
    }
  };

  const openPasswordDialog = (employee) => {
    setSelectedEmployee(employee);
    setNewPassword('');
    setIsPasswordDialogOpen(true);
  };

  const openDeleteDialog = (employee) => {
    setSelectedEmployee(employee);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      username: '',
      password: '',
      name: '',
      phone: '',
      email: '',
      salary: ''
    });
  };

  const filteredEmployees = employees.filter(employee => {
    return employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.phone?.includes(searchTerm) ||
      employee.username?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="employees-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Employees</h1>
          <p className="text-slate-500 mt-1">Manage employee accounts</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            data-testid="create-employee-btn"
            className="bg-brand-navy text-white hover:bg-brand-navy/90 shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Employee
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, phone or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-employees"
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Name</TableHead>
                <TableHead className="font-semibold text-slate-600">Username</TableHead>
                <TableHead className="font-semibold text-slate-600">Phone</TableHead>
                <TableHead className="font-semibold text-slate-600">Email</TableHead>
                <TableHead className="font-semibold text-slate-600">Salary</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
                {isAdmin && <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.map((employee) => (
                <TableRow key={employee.id} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium">{employee.name}</TableCell>
                  <TableCell className="font-mono text-sm">{employee.username}</TableCell>
                  <TableCell>
                    {employee.phone ? (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3 w-3 text-slate-400" />
                        {employee.phone}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {employee.email ? (
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-slate-400" />
                        {employee.email}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="font-semibold">{formatCurrency(employee.salary)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge className={employee.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {employee.is_active ? (
                          <><UserCheck className="h-3 w-3 mr-1" /> Active</>
                        ) : (
                          <><UserX className="h-3 w-3 mr-1" /> Inactive</>
                        )}
                      </Badge>
                      {employee.role === 'admin' && (
                        <Badge className="bg-purple-100 text-purple-700">
                          Admin
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" data-testid={`employee-actions-${employee.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => handleEdit(employee)} className="cursor-pointer">
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openPasswordDialog(employee)} className="cursor-pointer">
                            <Key className="h-4 w-4 mr-2" />
                            Change Password
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleToggleAdmin(employee)} 
                            className={`cursor-pointer ${employee.role === 'admin' ? 'text-amber-600' : 'text-blue-600'}`}
                          >
                            <UserCog className="h-4 w-4 mr-2" />
                            {employee.role === 'admin' ? 'Remove Admin Access' : 'Grant Admin Access'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {employee.is_active ? (
                            <DropdownMenuItem 
                              onClick={() => handleDeactivate(employee.id)} 
                              className="cursor-pointer text-amber-600"
                            >
                              <UserX className="h-4 w-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleReactivate(employee)} 
                              className="cursor-pointer text-emerald-600"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Reactivate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => openDeleteDialog(employee)} 
                            className="cursor-pointer text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filteredEmployees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    No employees found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">
              {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
            </DialogTitle>
            <DialogDescription>
              {editingEmployee ? 'Update employee information' : 'Enter employee details'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="input-employee-name"
                placeholder="Enter full name"
              />
            </div>
            {!editingEmployee && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    data-testid="input-employee-username"
                    placeholder="Enter username"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    data-testid="input-employee-password"
                    placeholder="Enter password"
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="input-employee-phone"
                  placeholder="Enter phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="input-employee-email"
                  placeholder="Enter email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Monthly Salary (₹)</Label>
              <Input
                type="number"
                value={formData.salary}
                onChange={(e) => setFormData(prev => ({ ...prev, salary: e.target.value }))}
                data-testid="input-employee-salary"
                placeholder="Enter salary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              data-testid="submit-employee-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!formData.name || (!editingEmployee && (!formData.username || !formData.password))}
            >
              {editingEmployee ? 'Update Employee' : 'Add Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy flex items-center gap-2">
              <Key className="h-5 w-5" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{selectedEmployee?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-employee-password"
                placeholder="Enter new password (min 4 characters)"
              />
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
              <strong>Note:</strong> The employee will need to use this new password for their next login.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleChangePassword}
              data-testid="confirm-change-password-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!newPassword || newPassword.length < 4}
            >
              <Key className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Employee Permanently
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong>{selectedEmployee?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg text-sm text-red-700 space-y-2">
              <p><strong>Warning:</strong> This action cannot be undone.</p>
              <p>This will permanently delete:</p>
              <ul className="list-disc list-inside ml-2">
                <li>Employee account and login credentials</li>
                <li>All attendance records</li>
                <li>All salary records</li>
                <li>All leave applications</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteEmployee}
              data-testid="confirm-delete-employee-btn"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
